import moment from 'moment';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import React, { forwardRef, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import InputField from '../../../../../../../components/inputField/InputField';
import { ApplicationContext } from '../../../../../../../config/Router';
import Modal from '../../../../../../../core/components/Modal';
import InputTextLengthCounter from '../../../../../../../core/components/inputs/inputTextLengthCounter/InputTextLengthCounter';
import InputTypes from '../../../../../../../core/enums/InputTypes';
import BaseForm from '../../../../../../../core/page/components/baseForm/BaseForm';
import { arrayToDate, arrayToDateString } from '../../../../../../../core/utils/DateMixins';
import { assignRefs } from '../../../../../../../core/utils/Global';
import apuracaoMetaService from '../../../../../../../service/ApuracaoMetaService';
import indicadorService from '../../../../../../../service/IndicadorService';
import outraApuracaoService from '../../../../../../../service/OutraApuracaoService';
import { useUsuarioLogado } from '../../../../../Utils';
import styles from './ModalApurarResultado.module.scss';

const modalApurarResultadoInputs = [
    {
        name: 'dataReferencia',
        label: 'Data de Referencia',
        type: InputTypes.DATE,
        form: { use: true, validate: { required: true } },
    },
    {
        name: 'dataApuracao',
        label: 'Data de Apuração',
        type: InputTypes.DATE,
        form: { use: true, validate: { required: true } },
    },
    {
        name: 'descValorApurado',
        label: 'Valor Apurado',
        type: InputTypes.STRING,
        form: { use: true, validate: { required: true } },
    },
    {
        name: 'usuario',
        label: 'Usuário',
        type: InputTypes.STRING,
        form: { use: true },
    },
];

function ModalApurarResultado({ ...props }, ref) {
    const modalRef = useRef(null);
    const formRef = useRef(null);
    const firstInput = useRef(null);
    const { id: idIndicador } = useParams();
    const user = useUsuarioLogado();
    const { refreshContext } = useContext(ApplicationContext);

    const Inputs = ({ formik, formController }) => (
        <form onSubmit={formik.handleSubmit} className={`p-grid p-fluid p-col-12 ${styles.form}`}>
            <InputField
                id="dataReferencia"
                formController={formController}
                formik={formik}
                cols="12 4"
            >
                <Calendar
                    ref={firstInput}
                    value={formik.values.dataReferencia}
                    onChange={formik.handleChange}
                    dateFormat="dd/mm/yy"
                    locale="ptBr"
                    mask="99/99/9999"
                    disabled={formik.values.view}
                />
            </InputField>
            <InputField
                id="dataApuracao"
                formController={formController}
                formik={formik}
                cols="12 4"
            >
                <Calendar
                    value={formik.values.dataApuracao}
                    onChange={formik.handleChange}
                    dateFormat="dd/mm/yy"
                    locale="ptBr"
                    mask="99/99/9999"
                    disabled={formik.values.view}
                />
            </InputField>
            <InputField
                id="descValorApurado"
                formController={formController}
                formik={formik}
                cols="12 4"
            >
                <InputTextLengthCounter maxLen={10}>
                    <InputText
                        value={formik.values.descValorApurado}
                        onChange={formik.handleChange}
                        disabled={formik.values.view}
                    />
                </InputTextLengthCounter>
            </InputField>
            <InputField id="usuario" formController={formController} formik={formik} cols="12 4">
                <InputText value={formik.values.usuario} onChange={formik.handleChange} disabled />
            </InputField>
            <div className={`buttonsRight ${styles.containerButtons}`}>
                <Button
                    label={formik.values.view ? 'Fechar' : 'Cancelar'}
                    className={formik.values.view ? 'p-button-info' : 'p-button-danger'}
                    type="button"
                    icon="pi pi-times"
                    onClick={() => modalRef.current.hide()}
                />
                {!formik.values.view && (
                    <Button
                        label="Salvar"
                        className="p-button-info"
                        type="submit"
                        icon="pi pi-check"
                        onClick={() => formController.addCountClick()}
                    />
                )}
            </div>
        </form>
    );

    const ModalBody = ({ modalData }) => {
        const { loadMetas, meta, view = false, outrasApuracoes = false } = modalData;
        return (
            <>
                <BaseForm
                    ref={formRef}
                    onSubmit={(dados) => {
                        if (outrasApuracoes) {
                            return outraApuracaoService.salvar(dados);
                        } else {
                            return apuracaoMetaService.salvar(dados);
                        }
                    }}
                    redirectOnSave={false}
                    actionOnSave={({ success }) => {
                        if (success) {
                            indicadorService.getCabecalho(idIndicador).then((response) => {
                                refreshContext({
                                    ...response,
                                    dataProximaApuracao: arrayToDateString(
                                        response?.dataProximaApuracao
                                    ),
                                });
                            });
                            if (loadMetas) {
                                modalRef.current.hide();
                                loadMetas();
                            }
                        }
                    }}
                    buscarOnLoad={false}
                    initialValues={modalApurarResultadoInputs}
                    prepareInitialValues={async (data) => {
                        setTimeout(() => firstInput.current.getInput().focus(), 500);

                        const ajustaData = (date) => {
                            if (typeof date === 'string') {
                                const [dia, mes, ano] = date.split('/');
                                date = moment(`${ano}-${mes}-${dia}`).toDate();
                            } else if (Array.isArray(date)) {
                                date = arrayToDate(date);
                            }
                            return date;
                        };

                        const dataReferencia = ajustaData(meta?.apuracaoMeta?.dataReferencia);
                        const dataApuracao = ajustaData(meta?.apuracaoMeta?.dataApuracao);

                        return {
                            ...data,
                            ...(meta?.apuracaoMeta ?? {}),
                            dataReferencia,
                            dataApuracao,
                            meta,
                            indicador: { id: idIndicador },
                            usuario: user?.nomeApresentacao,
                            view,
                        };
                    }}
                >
                    <Inputs />
                </BaseForm>
            </>
        );
    };

    return (
        <Modal
            header={
                <>
                    <i className={`pi pi-sliders-h ${styles.iconHeader}`} />
                    Apurar Resultado
                </>
            }
            style={{ maxWidth: '70vw' }}
            ref={assignRefs(ref, modalRef)}
            {...props}
        >
            <ModalBody />
        </Modal>
    );
}

export default forwardRef(ModalApurarResultado);
